import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Col, Container, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { TextInputform, TextArea, DropDown } from "../../components/Forms";
import { Buttons } from "../../components/Buttons";
import NotifyData from "../../components/NotifyData";
import {
  fetchCustomers,
  addCustomer,
  updateCustomer,
} from "../../slice/CustomerSlice";
import { generateCustomerNoByAreaApi } from "../../services/CustomerService";

const CustomerCreation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { customers, plans, areas, staff } = useSelector(
    (state) => state.customers
  );
  const prevAreaRef = useRef("");
  const userData = JSON.parse(localStorage.getItem("user")) || {};
  const currentUserId = userData.user_id;
  const isEditMode = !!id;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const customerSchema = [
    { name: "name", formLabel: "Customer Name", type: "text", required: true },
    { name: "phone", formLabel: "Phone Number", type: "text", required: true },
    { name: "address", textlabel: "Address", type: "textarea", required: true },
    { name: "area_id", textlabel: "Area", type: "dropdown", required: true },
    {
      name: "customer_no",
      formLabel: "Customer No",
      type: "text",
      required: true,
      disabled: false,
    },
    {
      name: "staff_id",
      textlabel: "Employee",
      type: "dropdown",
      required: true,
    },
    { name: "box_no", formLabel: "Box Number", type: "text", required: true },
    { name: "plan_id", textlabel: "Plan", type: "dropdown", required: true },
  ];

  const [formData, setFormData] = useState({
    name: "",
    customer_no: "",
    phone: "",
    address: "",
    area_id: "",
    box_no: "",
    plan_id: "",
    plan_prize: "",
    staff_id: "",
  });

  useEffect(() => {
    dispatch(fetchCustomers(""));
  }, [dispatch]);

  useEffect(() => {
    if (isEditMode && customers.length > 0) {
      const customer = customers.find((c) => c.customer_id === id);
      if (customer) {
        setFormData({
          name: customer.name || "",
          customer_no: customer.customer_no || "",
          phone: customer.phone || "",
          address: customer.address || "",
          area_id: customer.area_id || "",
          box_no: customer.box_no || "",
          plan_id: customer.plan_id || "",
          plan_prize: customer.plan_prize || "",
          staff_id: customer.staff_id || "",
        });
      }
    }
  }, [id, customers, isEditMode]);

  useEffect(() => {
    const generateCustomerNo = async () => {
      if (formData.area_id) {
        if (isEditMode && prevAreaRef.current === "") {
          prevAreaRef.current = formData.area_id;
          return;
        }

        try {
          const customerNo = await generateCustomerNoByAreaApi(
            formData.area_id
          );
          setFormData((prev) => ({ ...prev, customer_no: customerNo }));
          prevAreaRef.current = formData.area_id;
        } catch (error) {
          NotifyData("Failed to generate Customer No", "error");
        }
      }
    };

    generateCustomerNo();
  }, [formData.area_id, isEditMode]);

  useEffect(() => {
    return () => {
      prevAreaRef.current = "";
    };
  }, []);

  useEffect(() => {
    if (formData.plan_id) {
      const selectedPlan = plans.find(
        (plan) => plan.plan_id === formData.plan_id
      );
      if (selectedPlan) {
        setFormData((prev) => ({
          ...prev,
          plan_prize: selectedPlan.plan_prize || "",
        }));
      }
    }
  }, [formData.plan_id, plans]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    if (
      !formData.name ||
      !formData.customer_no ||
      !formData.area_id ||
      !formData.box_no ||
      !formData.plan_id ||
      !formData.staff_id
    ) {
      NotifyData(
        `${
          isEditMode ? "Customer Update" : "Customer Creation"
        } Failed: All fields are required`,
        "error"
      );
      setIsSubmitting(false);
      return;
    }

    if (!currentUserId) {
      NotifyData("Operation Failed: User not authenticated", "error");
      setIsSubmitting(false);
      return;
    }

    const customerData = {
      name: formData.name,
      customer_no: formData.customer_no,
      phone: formData.phone,
      address: formData.address,
      area_id: formData.area_id,
      box_no: formData.box_no,
      plan_id: formData.plan_id,
      plan_prize: formData.plan_prize,
      staff_id: formData.staff_id,
      current_user_id: currentUserId,
      ...(isEditMode && { customer_id: id }),
    };

    try {
      const action = isEditMode
        ? updateCustomer(customerData)
        : addCustomer(customerData);
      const msg = await dispatch(action).unwrap();
      NotifyData(msg, "success");
      navigate("/customer");
    } catch (error) {
      NotifyData(error.message, "error");
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/customer");
  };

  return (
    <div id="main">
      <Container fluid className="p-3">
        <Row className="">
          {customerSchema.map((field, index) => {
            return (
              <Col lg="3" md="6" xs="12" className="py-3" key={index}>
                {field.type === "dropdown" ? (
                  <DropDown
                    textlabel={field.textlabel}
                    name={field.name}
                    value={formData[field.name] || ""}
                    onChange={handleChange}
                    options={
                      field.name === "plan_id"
                        ? plans.map((plan) => ({
                            value: plan.plan_id,
                            label: `${plan.plan_name} (${plan.plan_prize})`,
                          }))
                        : field.name === "area_id"
                        ? areas.map((area) => ({
                            value: area.area_id,
                            label: area.area_name,
                          }))
                        : field.name === "staff_id"
                        ? staff.length > 0
                          ? staff.map((employee) => ({
                              value: employee.staff_id,
                              label: employee.staff_name,
                            }))
                          : [{ value: "", label: "No staff available" }]
                        : []
                    }
                  />
                ) : field.type === "textarea" ? (
                  <TextArea
                    textlabel={field.textlabel}
                    name={field.name}
                    value={formData[field.name] || ""}
                    onChange={handleChange}
                  />
                ) : (
                  <TextInputform
                    formLabel={field.formLabel}
                    PlaceHolder={field.formLabel}
                    name={field.name}
                    value={formData[field.name] || ""}
                    formtype={field.type}
                    onChange={handleChange}
                    className={field.classname || ""}
                    readOnly={field.disabled || false}
                  />
                )}
              </Col>
            );
          })}
        </Row>
        <div className="align-items-end justify-content-center mt-5 d-flex custom-create">
          <div className="p-4">
            <Buttons
              btnlabel={
                isSubmitting
                  ? isEditMode
                    ? "Updating..."
                    : "Submitting..."
                  : isEditMode
                  ? "Update"
                  : "Submit"
              }
              className="border-0 submit-btn"
              onClick={handleSubmit}
              disabled={isSubmitting}
            />
          </div>
          <div className="p-4">
            <Buttons
              btnlabel={<>Cancel</>}
              onClick={handleCancel}
              className="border-0 can-btn"
              disabled={isSubmitting}
            />
          </div>
        </div>
      </Container>
    </div>
  );
};

export default CustomerCreation;
